import {
  get,
  getNum,
  getStr,
  getTable,
  isDateMDY,
  isEmail,
  isPhone,
  isYes,
  lt,
  toDate,
  toPct,
} from '@shared';

// R1: Beneficiary percentages must sum to 100% within each class type (Primary & Contingent).
const ruleBeneficiaryPercentSums: Rule = {
  id: 'beneficiary.percent.sum',
  check: (form): RuleResult => {
    const rows = getTable(form, 'beneficiaries');
    const sumByType: Record<string, number> = {};
    let badCell = false;
    for (const r of rows) {
      const pctRaw = String(r['classPercentage'] ?? '');
      const pct = toPct(pctRaw);
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        badCell = true;
        break;
      }
      const type = String(r['classType'] ?? '').toLowerCase(); // 'primary' | 'contingent'
      sumByType[type] = (sumByType[type] ?? 0) + pct;
    }
    if (badCell) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'BENEFICIARY_PERCENT_INVALID',
        message: 'Each beneficiary % must be a number between 0 and 100.',
        refs: ['beneficiaries'],
        category: 'formatting',
      };
    }
    const errors: string[] = [];
    (['primary', 'contingent'] as const).forEach(k => {
      if ((sumByType[k] ?? 0) > 0 && Math.round(sumByType[k] ?? 0) !== 100) {
        errors.push(`${k} = ${sumByType[k] ?? 0}%`);
      }
    });
    if (errors.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'BENEFICIARY_SUM_100',
        message: `Beneficiary percentages must total 100% separately for Primary and Contingent (${errors.join(
          '; ',
        )}).`,
        refs: ['beneficiaries'],
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R2: In-force and pending coverage table coherence validation
const ruleInForceCoverageTableCoherence: Rule = {
  id: 'inforce.coverage.table.coherence',
  when: form =>
    isYes(get(form, 'inForceCoverageQ1')) ||
    isYes(get(form, 'inForceCoverageQ2')) ||
    isYes(get(form, 'inForceCoverageQ3')) ||
    isYes(get(form, 'inForceCoverageQ4')) ||
    isYes(get(form, 'inForceCoverageQ5')),
  check: (form): RuleResult => {
    const rows = getTable(form, 'inForcePendingCoverageTable');
    if (!rows.length) return { ok: true };
    const errs: string[] = [];
    rows.forEach((r, i) => {
      const idx = i + 1;
      // If "will be replaced" = Yes, Company & Policy Number should be present
      if (isYes(r['willBeReplaced'])) {
        if (!String(r['companyName'] ?? '').trim())
          errs.push(`Row ${idx}: Company Name required when "Will Be Replaced" is Yes.`);
        if (!String(r['policyNumber'] ?? '').trim())
          errs.push(`Row ${idx}: Policy Number required when "Will Be Replaced" is Yes.`);
      }
      // Year Issued sanity
      const y = String(r['yearIssued'] ?? '').trim();
      if (y && !/^\d{4}$/.test(y)) errs.push(`Row ${idx}: Year Issued must be YYYY.`);
      // Amount numeric sanity
      const amt = getNum({ amt: r['amount'] }, 'amt');
      if (String(r['amount'] ?? '').trim() && !Number.isFinite(amt))
        errs.push(`Row ${idx}: Amount must be a valid number.`);
    });
    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'INFORCE_TABLE_SANITY',
        message: errs.join(' '),
        refs: ['inForcePendingCoverageTable'],
        category: 'formatting',
      };
    }
    return { ok: true };
  },
};

// R3: Life and Annuity Replacement table coherence
const ruleLifeAnnuityReplacementCoherence: Rule = {
  id: 'life.annuity.replacement.coherence',
  when: form => isYes(get(form, 'policyReplacement')),
  check: (form): RuleResult => {
    const rows = getTable(form, 'lifeAnnuityReplacementTable');
    if (!rows.length) return { ok: true };
    const errs: string[] = [];
    rows.forEach((r, i) => {
      const idx = i + 1;
      // Required fields validation
      if (!String(r['companyName'] ?? '').trim())
        errs.push(`Row ${idx}: Company Name is required.`);
      if (!String(r['policyContractNumber'] ?? '').trim())
        errs.push(`Row ${idx}: Policy/Contract Number is required.`);
      // Date validation
      const dateStr = String(r['dateOfIssue'] ?? '').trim();
      if (dateStr && !isDateMDY(dateStr))
        errs.push(`Row ${idx}: Date of Issue must be mm/dd/yyyy.`);
      // Face amount sanity (insurance only)
      const face = getNum({ amt: r['faceAmount'] }, 'amt');
      if (String(r['faceAmount'] ?? '').trim() && !Number.isFinite(face))
        errs.push(`Row ${idx}: Face Amount must be a valid number.`);
      // Annual premium sanity
      const premium = getNum({ amt: r['annualPremium'] }, 'amt');
      if (String(r['annualPremium'] ?? '').trim() && !Number.isFinite(premium))
        errs.push(`Row ${idx}: Annual Premium must be a valid number.`);
    });
    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'REPLACEMENT_TABLE_SANITY',
        message: errs.join(' '),
        refs: ['lifeAnnuityReplacementTable'],
        category: 'formatting',
      };
    }
    return { ok: true };
  },
};

// R4: Identity document verification sanity (issue < expiration for trustees)
const ruleDocDateOrder: Rule = {
  id: 'identity.docs.date.order',
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    // Trustee verification documents
    const issueDate = toDate(get(form, 'trusteeVerificationIssueDate'));
    const expDate = toDate(get(form, 'trusteeVerificationExpirationDate'));
    if (issueDate && expDate) {
      if (!lt(issueDate, expDate)) {
        errs.push('Trustee Verification: Issue date must be before expiration date.');
        refs.push('trusteeVerificationIssueDate', 'trusteeVerificationExpirationDate');
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'DOC_DATE_ORDER',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R5: Premium payment sanity checks
const rulePremiumSanity: Rule = {
  id: 'premium.sanity',
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    const premiumMethod = String(get(form, 'premiumMethod') ?? '');

    // Single Premium validation
    if (premiumMethod === 'single-premium') {
      const amt = getNum(form, 'premiumSingleAmount');
      if (!Number.isNaN(amt) && amt <= 0) {
        errs.push('Single Premium Amount must be greater than 0.');
        refs.push('premiumSingleAmount');
      }
    }

    // Annual Premium validation
    if (premiumMethod === 'annual-premium') {
      const amt = getNum(form, 'premiumAnnualAmount');
      if (!Number.isNaN(amt) && amt <= 0) {
        errs.push('Annual Premium Amount must be greater than 0.');
        refs.push('premiumAnnualAmount');
      }
    }

    // Annual Premium with Additional First Year validation
    if (premiumMethod === 'annual-with-first-year') {
      const total = getNum(form, 'premiumAnnualWithFirstYearTotal');
      const additional = getNum(form, 'premiumAnnualFirstYearAdditional');
      if (!Number.isNaN(total) && total <= 0) {
        errs.push('Annual Premium Total must be greater than 0.');
        refs.push('premiumAnnualWithFirstYearTotal');
      }
      if (!Number.isNaN(additional) && additional < 0) {
        errs.push('Additional First Year Premium cannot be negative.');
        refs.push('premiumAnnualFirstYearAdditional');
      }
    }

    // LTC Premium validation
    const ltcPremiumMethod = String(get(form, 'ltcPremiumMethod') ?? '');
    if (ltcPremiumMethod === 'single-premium') {
      const amt = getNum(form, 'ltcSinglePremiumAmount');
      if (!Number.isNaN(amt) && amt <= 0) {
        errs.push('LTC Single Premium Amount must be greater than 0.');
        refs.push('ltcSinglePremiumAmount');
      }
    }
    if (ltcPremiumMethod === 'annual-premium') {
      const amt = getNum(form, 'ltcAnnualPremiumAmount');
      const years = getNum(form, 'ltcAnnualPremiumYears');
      if (!Number.isNaN(amt) && amt <= 0) {
        errs.push('LTC Annual Premium Amount must be greater than 0.');
        refs.push('ltcAnnualPremiumAmount');
      }
      if (!Number.isNaN(years) && (years <= 0 || years > 100)) {
        errs.push('LTC Premium Years must be between 1 and 100.');
        refs.push('ltcAnnualPremiumYears');
      }
    }
    if (ltcPremiumMethod === 'annual-with-first-year') {
      const amt = getNum(form, 'ltcAnnualPremiumWithFirstYearAmount');
      const years = getNum(form, 'ltcAnnualPremiumWithFirstYearYears');
      const extra = getNum(form, 'ltcAnnualPremiumFirstYearExtra');
      if (!Number.isNaN(amt) && amt <= 0) {
        errs.push('LTC Annual Premium Amount must be greater than 0.');
        refs.push('ltcAnnualPremiumWithFirstYearAmount');
      }
      if (!Number.isNaN(years) && (years <= 0 || years > 100)) {
        errs.push('LTC Premium Years must be between 1 and 100.');
        refs.push('ltcAnnualPremiumWithFirstYearYears');
      }
      if (!Number.isNaN(extra) && extra < 0) {
        errs.push('LTC Additional First Year Premium cannot be negative.');
        refs.push('ltcAnnualPremiumFirstYearExtra');
      }
    }

    // Bank routing number validation (must be 9 digits)
    const routing = String(get(form, 'bankRoutingNumber') ?? '').trim();
    if (routing && !/^\d{9}$/.test(routing)) {
      errs.push('Bank Routing Number must be exactly 9 digits.');
      refs.push('bankRoutingNumber');
    }

    // Draft day validation (must be 1-28)
    const draftDay = getNum(form, 'bankDraftDay');
    if (!Number.isNaN(draftDay) && (draftDay < 1 || draftDay > 28)) {
      errs.push('Bank Draft Day must be between 1 and 28.');
      refs.push('bankDraftDay');
    }

    // Temporary insurance amount validation
    const tempAmt = getNum(form, 'temporaryInsuranceAmountReceived');
    if (!Number.isNaN(tempAmt) && tempAmt < 0) {
      errs.push('Temporary Insurance Amount cannot be negative.');
      refs.push('temporaryInsuranceAmountReceived');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PREMIUM_SANITY',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R6: Replacement consistency between applicant answers and financial professional
const ruleReplacementConsistency: Rule = {
  id: 'replacement.consistency',
  when: form =>
    get(form, 'policyReplacement') != null ||
    get(form, 'financialReplacementQ1') != null ||
    get(form, 'financialReplacementQ2') != null,
  check: (form): RuleResult => {
    const conflicts: string[] = [];

    const applicantReplacementYes = isYes(get(form, 'policyReplacement'));
    const inForceYes = isYes(get(form, 'inForceCoverageQ1'));
    const fpReplacementQ1Yes = isYes(get(form, 'financialReplacementQ1'));

    // Check consistency between applicant and FP answers
    if (applicantReplacementYes && !fpReplacementQ1Yes && fpReplacementQ1Yes !== undefined) {
      conflicts.push(
        'Applicant marked policy replacement as Yes but Financial Professional marked replacement as No.',
      );
    }
    if (!applicantReplacementYes && fpReplacementQ1Yes && applicantReplacementYes !== undefined) {
      conflicts.push(
        'Financial Professional marked replacement as Yes but Applicant marked policy replacement as No.',
      );
    }

    // Check if in-force coverage exists but no replacement indicated
    if (inForceYes && !applicantReplacementYes && applicantReplacementYes !== undefined) {
      conflicts.push(
        'In-force or pending coverage exists but policy replacement not indicated.',
      );
    }

    if (conflicts.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'REPLACEMENT_CONFLICT',
        message: conflicts.join(' '),
        refs: [
          'policyReplacement',
          'inForceCoverageQ1',
          'financialReplacementQ1',
          'financialReplacementQ2',
        ],
        category: 'general',
      };
    }
    return { ok: true };
  },
};

// R7: Contact information format validation
const ruleContactFormatValidation: Rule = {
  id: 'contact.format.validation',
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    // Email validations
    const irrevocableEmail = getStr(form, 'irrevocableBeneficiaryEmail');
    if (irrevocableEmail && !isEmail(irrevocableEmail)) {
      errs.push('Irrevocable beneficiary email format looks invalid.');
      refs.push('irrevocableBeneficiaryEmail');
    }

    // Phone validations
    const payorPhone = getStr(form, 'payorTelephoneNumber');
    if (payorPhone && !isPhone(payorPhone)) {
      errs.push('Payor telephone number format looks invalid.');
      refs.push('payorTelephoneNumber');
    }

    // Beneficiary table email/phone validation
    const beneficiaries = getTable(form, 'beneficiaries');
    beneficiaries.forEach((b, i) => {
      const email = String(b['email'] ?? '').trim();
      if (email && !isEmail(email)) {
        errs.push(`Beneficiary row ${i + 1}: Email format looks invalid.`);
        refs.push('beneficiaries');
      }
      const phone = String(b['telephone'] ?? '').trim();
      if (phone && !isPhone(phone)) {
        errs.push(`Beneficiary row ${i + 1}: Phone format looks invalid.`);
        refs.push('beneficiaries');
      }
    });

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'CONTACT_FORMAT',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'formatting',
      };
    }
    return { ok: true };
  },
};

// R8: LTC Policy Replacement premium comparison sanity
const ruleLTCReplacementSanity: Rule = {
  id: 'ltc.replacement.sanity',
  when: form => isYes(get(form, 'inForceCoverageQ3')) || isYes(get(form, 'financialReplacementQ2')),
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    // Requested SecureCare policy premium validation
    const requestedType = String(get(form, 'requestedPremiumType') ?? '');
    if (requestedType === 'single') {
      const amt = getNum(form, 'requestedPremiumSingleAmount');
      if (!Number.isNaN(amt) && amt <= 0) {
        errs.push('Requested Single Premium Amount must be greater than 0.');
        refs.push('requestedPremiumSingleAmount');
      }
    }
    if (requestedType === 'annual') {
      const amt = getNum(form, 'requestedPremiumAnnualAmount');
      const years = getNum(form, 'requestedYearsPremiumPayment');
      if (!Number.isNaN(amt) && amt <= 0) {
        errs.push('Requested Annual Premium Amount must be greater than 0.');
        refs.push('requestedPremiumAnnualAmount');
      }
      if (!Number.isNaN(years) && (years <= 0 || years > 100)) {
        errs.push('Requested Years of Premium Payment must be between 1 and 100.');
        refs.push('requestedYearsPremiumPayment');
      }
    }

    // Existing Policy 1 validation
    const existing1Premium = getNum(form, 'existing1CurrentAnnualPremium');
    const existing1TotalPaid = getNum(form, 'existing1TotalPremiumPaidToDate');
    if (!Number.isNaN(existing1Premium) && existing1Premium < 0) {
      errs.push('Existing Policy 1: Current Annual Premium cannot be negative.');
      refs.push('existing1CurrentAnnualPremium');
    }
    if (!Number.isNaN(existing1TotalPaid) && existing1TotalPaid < 0) {
      errs.push('Existing Policy 1: Total Premium Paid to Date cannot be negative.');
      refs.push('existing1TotalPremiumPaidToDate');
    }

    // Existing Policy 2 validation
    const existing2Premium = getNum(form, 'existing2CurrentAnnualPremium');
    const existing2TotalPaid = getNum(form, 'existing2TotalPremiumPaidToDate');
    if (!Number.isNaN(existing2Premium) && existing2Premium < 0) {
      errs.push('Existing Policy 2: Current Annual Premium cannot be negative.');
      refs.push('existing2CurrentAnnualPremium');
    }
    if (!Number.isNaN(existing2TotalPaid) && existing2TotalPaid < 0) {
      errs.push('Existing Policy 2: Total Premium Paid to Date cannot be negative.');
      refs.push('existing2TotalPremiumPaidToDate');
    }

    // Rider replacement validation
    const riderRequestedType = String(get(form, 'riderRequestedPremiumType') ?? '');
    if (riderRequestedType === 'single') {
      const amt = getNum(form, 'riderRequestedPremiumSingleAmount');
      if (!Number.isNaN(amt) && amt <= 0) {
        errs.push('Rider Requested Single Premium Amount must be greater than 0.');
        refs.push('riderRequestedPremiumSingleAmount');
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'LTC_REPLACEMENT_SANITY',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R9: Premium financing sanity checks
const rulePremiumFinancingSanity: Rule = {
  id: 'premium.financing.sanity',
  when: form =>
    get(form, 'premiumFinancingNetWorth') != null ||
    get(form, 'premiumFinancingProposedLender') != null,
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    const netWorth = getNum(form, 'premiumFinancingNetWorth');
    if (!Number.isNaN(netWorth) && netWorth < 0) {
      errs.push('Premium Financing: Net Worth cannot be negative.');
      refs.push('premiumFinancingNetWorth');
    }

    // If premium financing fields are filled, ensure critical fields are present
    const hasFinancingInfo =
      get(form, 'premiumFinancingNetWorth') != null ||
      get(form, 'premiumFinancingProposedLender') != null;

    if (hasFinancingInfo) {
      if (!getStr(form, 'premiumFinancingAdvisorName')) {
        errs.push('Premium Financing: Advisor Name is required when premium financing is used.');
        refs.push('premiumFinancingAdvisorName');
      }
      if (!get(form, 'premiumFinancingCaseMeetsGuidelines')) {
        errs.push(
          'Premium Financing: Confirmation that case meets guidelines is required.',
        );
        refs.push('premiumFinancingCaseMeetsGuidelines');
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'PREMIUM_FINANCING_SANITY',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R10: Policy initial premium validation (for policies 1-4)
const rulePolicyInitialPremiumSanity: Rule = {
  id: 'policy.initial.premium.sanity',
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    const policyNumbers = ['policy1', 'policy2', 'policy3', 'policy4'] as const;

    policyNumbers.forEach(policyNum => {
      const option = String(get(form, `${policyNum}InitialPremiumOption`) ?? '');

      if (option === 'conversions-exchanges') {
        const amt = getNum(form, `${policyNum}InitialPremiumConversionsExchangesAmount`);
        if (!Number.isNaN(amt) && amt <= 0) {
          errs.push(`${policyNum}: Conversions/Exchanges Amount must be greater than 0.`);
          refs.push(`${policyNum}InitialPremiumConversionsExchangesAmount`);
        }
      }

      if (option === 'new-business') {
        const amt = getNum(form, `${policyNum}InitialPremiumNewBusinessApplicationAmount`);
        if (!Number.isNaN(amt) && amt <= 0) {
          errs.push(`${policyNum}: New Business Application Amount must be greater than 0.`);
          refs.push(`${policyNum}InitialPremiumNewBusinessApplicationAmount`);
        }
      }

      const recurringAmt = getNum(form, `${policyNum}RecurringMonthlyPremiumAmount`);
      if (!Number.isNaN(recurringAmt) && recurringAmt < 0) {
        errs.push(`${policyNum}: Recurring Monthly Premium Amount cannot be negative.`);
        refs.push(`${policyNum}RecurringMonthlyPremiumAmount`);
      }

      const oneTimeAmt = getNum(form, `${policyNum}OneTimePremiumAdditionalAmount`);
      if (!Number.isNaN(oneTimeAmt) && oneTimeAmt < 0) {
        errs.push(`${policyNum}: One-Time Premium Additional Amount cannot be negative.`);
        refs.push(`${policyNum}OneTimePremiumAdditionalAmount`);
      }

      const depositAmt = getNum(form, `${policyNum}OneTimePremiumDepositAccountAmount`);
      if (!Number.isNaN(depositAmt) && depositAmt < 0) {
        errs.push(`${policyNum}: One-Time Premium Deposit Account Amount cannot be negative.`);
        refs.push(`${policyNum}OneTimePremiumDepositAccountAmount`);
      }
    });

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'POLICY_INITIAL_PREMIUM_SANITY',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R11: 1035 Exchange coherence - validate exchange details when payment method involves exchanges
const rule1035ExchangeCoherence: Rule = {
  id: '1035.exchange.coherence',
  when: form => {
    const paymentMethod = String(get(form, 'paymentMethod') ?? '');
    return (
      paymentMethod.includes('1035') ||
      paymentMethod === 'full_1035' ||
      paymentMethod === 'partial_1035'
    );
  },
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    const exchangeCompany = getStr(form, 'exchange-company-name');
    const exchangePolicyNumbers = getStr(form, 'exchange-policy-numbers');
    const exchangeCashValue = getNum(form, 'exchange-estimated-cash-value');
    const exchangeDate = getStr(form, 'exchange-estimated-date');

    if (!exchangeCompany) {
      errs.push('1035 Exchange: Company name is required.');
      refs.push('exchange-company-name');
    }
    if (!exchangePolicyNumbers) {
      errs.push('1035 Exchange: Policy number(s) required.');
      refs.push('exchange-policy-numbers');
    }
    if (Number.isNaN(exchangeCashValue) || exchangeCashValue <= 0) {
      errs.push('1035 Exchange: Estimated cash value must be greater than 0.');
      refs.push('exchange-estimated-cash-value');
    }
    if (exchangeDate && !isDateMDY(exchangeDate)) {
      errs.push('1035 Exchange: Estimated date must be mm/dd/yyyy format.');
      refs.push('exchange-estimated-date');
    }

    // Partial 1035 specific validations
    const paymentMethod = String(get(form, 'paymentMethod') ?? '');
    if (paymentMethod === 'partial_1035' || paymentMethod === 'partial-1035') {
      const partialOwner = getStr(form, 'exchange-partial-policy-owner-name');
      const partialCompany = getStr(form, 'exchange-partial-issuing-company');
      const partialPolicyNum = getStr(form, 'exchange-partial-old-policy-number');

      if (!partialOwner) {
        errs.push('Partial 1035: Policy owner name is required.');
        refs.push('exchange-partial-policy-owner-name');
      }
      if (!partialCompany) {
        errs.push('Partial 1035: Issuing company is required.');
        refs.push('exchange-partial-issuing-company');
      }
      if (!partialPolicyNum) {
        errs.push('Partial 1035: Old policy number is required.');
        refs.push('exchange-partial-old-policy-number');
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: '1035_EXCHANGE_INCOMPLETE',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R12: Irrevocable beneficiary consistency - if marked as having irrevocable beneficiary, details must be present
const ruleIrrevocableBeneficiaryConsistency: Rule = {
  id: 'irrevocable.beneficiary.consistency',
  when: form => isYes(get(form, 'hasIrrevocableBeneficiary')),
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    const name = getStr(form, 'irrevocableBeneficiaryName');
    if (!name) {
      errs.push('Irrevocable beneficiary name is required when irrevocable beneficiary exists.');
      refs.push('irrevocableBeneficiaryName');
    }

    // Also check beneficiary table for irrevocable entries
    const beneficiaries = getTable(form, 'beneficiaries');
    const hasIrrevocableInTable = beneficiaries.some(b => isYes(b['irrevocable']));

    if (!hasIrrevocableInTable && isYes(get(form, 'hasIrrevocableBeneficiary'))) {
      errs.push(
        'Irrevocable beneficiary indicated but no beneficiary marked as irrevocable in table.',
      );
      refs.push('hasIrrevocableBeneficiary', 'beneficiaries');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'IRREVOCABLE_BENEFICIARY_INCONSISTENT',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R13: Representative report identity verification - ensure proper documentation when not meeting in person
const ruleRepReportIdentityVerification: Rule = {
  id: 'rep.report.identity.verification',
  when: form => get(form, 'repReportNotMetMethod') != null,
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    const notMetMethod = String(get(form, 'repReportNotMetMethod') ?? '');

    // If not meeting in person, identity docs must be selected
    if (notMetMethod) {
      const ownerDocs = get(form, 'repReportOwnerIdentityDocs');
      const ownerIdNumber = getStr(form, 'repReportOwnerIdentityNumber');

      if (!ownerDocs || (Array.isArray(ownerDocs) && ownerDocs.length === 0)) {
        errs.push(
          'Representative Report: Identity documentation type required when not meeting in person.',
        );
        refs.push('repReportOwnerIdentityDocs');
      }

      if (!ownerIdNumber) {
        errs.push(
          'Representative Report: Identification number required when not meeting in person.',
        );
        refs.push('repReportOwnerIdentityNumber');
      }

      // Check expiration date if provided
      const expirationDate = getStr(form, 'repReportOwnerIdentityExpiration');
      if (expirationDate && !isDateMDY(expirationDate)) {
        errs.push('Representative Report: Identity document expiration date must be mm/dd/yyyy.');
        refs.push('repReportOwnerIdentityExpiration');
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'REP_REPORT_IDENTITY_INCOMPLETE',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'carrier-requirements',
      };
    }
    return { ok: true };
  },
};

// R14: Business insurance validation - ensure proper documentation for business-owned policies
const ruleBusinessInsuranceValidation: Rule = {
  id: 'business.insurance.validation',
  when: form => get(form, 'repReportBusinessInsuranceType') != null,
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    const businessType = get(form, 'repReportBusinessInsuranceType');

    if (businessType) {
      const businessValue = getNum(form, 'repReportBusinessValue');
      const ownershipPercent = getNum(form, 'repReportBusinessOwnershipPercent');

      if (Number.isNaN(businessValue) || businessValue <= 0) {
        errs.push('Business Insurance: Business value must be greater than 0.');
        refs.push('repReportBusinessValue');
      }

      if (!Number.isNaN(ownershipPercent) && (ownershipPercent < 0 || ownershipPercent > 100)) {
        errs.push('Business Insurance: Ownership percentage must be between 0 and 100.');
        refs.push('repReportBusinessOwnershipPercent');
      }
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'high-confidence',
        code: 'BUSINESS_INSURANCE_INCOMPLETE',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'data-consistency',
      };
    }
    return { ok: true };
  },
};

// R15: Product suitability validation - ensure exchange reasoning is provided when applicable
const ruleProductSuitabilitySanity: Rule = {
  id: 'product.suitability.sanity',
  when: form => isYes(get(form, 'productSuitabilitySoldExistingProduct')),
  check: (form): RuleResult => {
    const errs: string[] = [];
    const refs: string[] = [];

    // If sold existing product that is being exchanged, reason must be provided
    const exchangeFeature = get(form, 'productSuitabilityExchangeFeature');
    const exchangeReason = getStr(form, 'productSuitabilityExchangeReason');

    if (exchangeFeature && !exchangeReason) {
      errs.push(
        'Product Suitability: Exchange reason required when exchanging existing product features.',
      );
      refs.push('productSuitabilityExchangeReason');
    }

    // Financial benefit should be documented
    const financialBenefit = getStr(form, 'productSuitabilityFinancialBenefit');
    if (!financialBenefit) {
      errs.push(
        'Product Suitability: Financial benefit explanation required for product suitability.',
      );
      refs.push('productSuitabilityFinancialBenefit');
    }

    if (errs.length) {
      return {
        ok: false,
        severity: 'medium-confidence',
        code: 'PRODUCT_SUITABILITY_INCOMPLETE',
        message: errs.join(' '),
        refs: Array.from(new Set(refs)),
        category: 'carrier-requirements',
      };
    }
    return { ok: true };
  },
};

// ---------- EXPORT ------------------------------------------------------------

export const securianAllStatesRules: Rule[] = [
  ruleBeneficiaryPercentSums,
  ruleInForceCoverageTableCoherence,
  ruleLifeAnnuityReplacementCoherence,
  ruleDocDateOrder,
  rulePremiumSanity,
  ruleReplacementConsistency,
  ruleContactFormatValidation,
  ruleLTCReplacementSanity,
  rulePremiumFinancingSanity,
  rulePolicyInitialPremiumSanity,
  rule1035ExchangeCoherence,
  ruleIrrevocableBeneficiaryConsistency,
  ruleRepReportIdentityVerification,
  ruleBusinessInsuranceValidation,
  ruleProductSuitabilitySanity,
];

export function evaluateSecurianNigo(
  form: PolicyFormSubmissionData,
  state?: string,
): RuleResult[] {
  return securianAllStatesRules
    .filter(r => !r.states || !state || r.states.includes(state))
    .filter(r => (r.when ? r.when(form) : true))
    .map(r => {
      try {
        return r.check(form);
      } catch (e) {
        return {
          ok: false,
          severity: 'high-confidence' as const,
          code: 'RULE_RUNTIME',
          message: `Rule ${r.id} failed: ${String(e)}`,
          category: 'general' as const,
        };
      }
    })
    .filter(res => !res.ok);
}
